import { forwardRef, useState } from 'react';

import { IconButton } from '@polyai/ui/components/atoms/IconButton';
import {
  VisibilityDisabledIcon,
  VisibilityIcon,
} from '@polyai/ui/components/atoms/Icons';
import type { InputProps } from '@polyai/ui/components/atoms/Input';
import { Input } from '@polyai/ui/components/atoms/Input';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';

import { PASSWORD_COPY } from './PasswordInput.uiCopy';

interface Props extends Omit<InputProps, 'type'> {}

const PasswordInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      label = PASSWORD_COPY.label,
      placeholder = PASSWORD_COPY.placeholder,
      disabled = false,
      errors = [],
      className,
      ...props
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false);

    const toggleVisibility = () => {
      setIsVisible(!isVisible);
    };

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          autoComplete="off"
          className={className}
          data-bitwarden-watching="false"
          data-bwignore="true"
          data-dashlane-ignore="true"
          data-lpignore="true"
          disabled={disabled}
          errors={errors}
          label={label}
          placeholder={placeholder}
          type={isVisible ? 'text' : 'password'}
          value={value}
          data-1p-ignore
          fullWidth
          {...props}
          onChange={onChange}
        />
        <div className="absolute right-sm2 top-[45px] -translate-y-1/2">
          <Tooltip
            content={
              isVisible
                ? PASSWORD_COPY.tooltips.hidePassword
                : PASSWORD_COPY.tooltips.showPassword
            }
            placement="top"
            trigger={['hover', 'focus']}
          >
            <IconButton
              aria-label={
                isVisible
                  ? PASSWORD_COPY.ariaLabels.hidePassword
                  : PASSWORD_COPY.ariaLabels.showPassword
              }
              disabled={disabled}
              onClick={toggleVisibility}
            >
              {isVisible ? (
                <VisibilityDisabledIcon className="w-sm1 h-sm1" />
              ) : (
                <VisibilityIcon className="w-sm1 h-sm1" />
              )}
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
